import React, { useState } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import Progress from './shared/Progress';
import { StatusBarAlert } from './statusBar/StatusBarAlert';
import { AssociateAttendee } from './associateAttendee/AssociateAttendee';
import { DisposableAlert } from './shared/DisposableAlert';
import { clearAlertText } from './../actions/index';
import SaveViewSkeleton from './shared/Skeletons/SaveViewSkeleton';
import { SaveToContainer } from './saveToContainer/calendar/SaveToContainer';
import { StatusBar } from './statusBar/StatusBar';
import { UninterruptedModeBanner } from './statusBar/UninterruptedModeBanner';
import officeApi from '../../api/officeApi';
import * as CalendarActions from '../actions/calendar';
import * as RootActions from '../actions/index';
import * as SalesforceRecordActions from '../actions/salesforceRecords';
import { getSalesforceContacts } from '../../api/emailsApi/calendar';
import { AddAttendee } from './addAttendee/AddAttendee';
import { SearchContainer } from './searchContainer/calendar/SearchContainer';
import { useInterval } from '../utils/hooks';
import SalesforceRecordFormContainer from './salesforceRecordForm/SalesforceRecordFormContainer';
import SalesforceRecordButton from './salesforceRecordButton/SalesforceRecordButton';

let isInitialized = false;

const Calendar = () => {
  const dispatch = useDispatch();
  const isOfficeInitialized = useSelector((state) => state.app.isOfficeInitialized);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const initializationError = useSelector((state) => state.app.initializationError);
  const isAssociatingAttendee = useSelector((state) => state.calendar.isAssociatingAttendee);
  const authFailure = useSelector((state) => state.auth.authenticationFailure);
  const requestingLogout = useSelector((state) => state.auth.requestingLogout);
  const isLoading = useSelector((state) => state.app.isLoading);
  const error = initializationError || authFailure;
  const alertText = useSelector((state) => state.app.alertText);
  // const eventAttendees = useSelector((state) => state.calendar.eventAttendees); // See comment on  line 55
  const isComposingEvent = useSelector((state) => state.calendar.isComposingEvent);
  const isAddingAttendee = useSelector((state) => state.calendar.addingAttendee);
  const isSelectingRecord = useSelector((state) => state.calendar.isSelectingRecord);
  const additionalObjectsAndFieldSets = useSelector((state) => state.mail.settings?.additionalObjectsAndFieldSets);
  const uninterruptedModeEnabled = useSelector((state) => state.mail.settings?.syncSettingsAndUsageReport?.uninterruptedModeEnabled);
  const hasRefreshTokenIssue = useSelector((state) => state.auth.user?.hasRefreshTokenIssue);
  const isSalesforceRecordFormOpen = useSelector((state) => state.salesforceRecords.isSalesforceRecordFormOpen);
  const [organizer, setOrganizer] = useState();

  // handle salesforce action click
  const handleSalesforceRecordClick = (action) => {
    // set the action
    dispatch(SalesforceRecordActions.setSalesforceRecordObjectApiName(action));
    // clear current actions to prevent multiple actions from being set
    dispatch(CalendarActions.composingEvent(false));
    dispatch(CalendarActions.addingAttendee(false));
    dispatch(CalendarActions.isSelectingRecord(false));
    dispatch(SalesforceRecordActions.setIsSalesforceRecordFormOpen(true));
    dispatch(RootActions.isLoading(true));
  };
  if (!isOfficeInitialized) {
    return (<Progress child={<SaveViewSkeleton />} />);
  }

  useInterval(() => {
    ownerChangeHandler();
  }, 1000);

  const ownerChangeHandler = async () => {
    if (isComposingEvent && isInitialized) {
      const eventOrganizer = await officeApi.getEventOrganizer();
      if (!organizer) {
        setOrganizer(eventOrganizer.emailAddress);
      } else if (organizer !== eventOrganizer.emailAddress) {
        // reload the add in if the organizer changes
        window.location.reload();
      }
    }
  };

  if (!isInitialized && isComposingEvent) {
    officeApi.onAttendeeChange(async () => {
      const [required, optional, organizer] = await Promise.all([...officeApi.getComposeEventAttendees()]);
      const newEventAttendees = {
        organizer,
        required: Array.from(new Set([...required])),
        optional: Array.from(new Set([...optional])),
      };
      /*
      This if check does not currently work since eventAttendees in this callback maintains the
      original value it had when the add-in is initially loaded.
      TODO: Since this check is useful to prevent this call from running when a user adds the same email twice, we
      should see if there is a way to get the current state of eventAttendees.
      */
      // if (JSON.stringify(newEventAttendees) !== JSON.stringify(eventAttendees)) {
      dispatch(RootActions.isLoading(true));
      const attendeeMapping = await getSalesforceContacts([newEventAttendees.organizer, ...newEventAttendees.required, ...newEventAttendees.optional]);
      batch(() => {
        dispatch(RootActions.isLoading(false));
        dispatch(CalendarActions.setSalesforceContactMap(attendeeMapping));
        dispatch(CalendarActions.setEventAttendees(newEventAttendees));
        dispatch(CalendarActions.attendeeChange());
      });
      // }
    });
    isInitialized = true;
  }

  return (
    <div className='app-container'>
      <div className='main-container'>
        <div className='app-notifications'>
          {uninterruptedModeEnabled && hasRefreshTokenIssue && <UninterruptedModeBanner />}
          {alertText &&
            <DisposableAlert child={<StatusBarAlert />}
              disposeFunction={() => {
                dispatch(clearAlertText());
              }}
              timeout={3000}
            />}
        </div>
        {isLoading && <Progress />}
        {isAuthenticated && !error && !requestingLogout && <>
          {isAddingAttendee ? <AddAttendee /> :
            isSelectingRecord ? <SearchContainer showBack={true} /> :
              isAssociatingAttendee ? <AssociateAttendee /> :
                isSalesforceRecordFormOpen ? <SalesforceRecordFormContainer /> :
                  <>
                    <div id='status-bar'>
                      <StatusBar />
                    </div>

                    {additionalObjectsAndFieldSets?.length > 0 &&
                      <div className='salesforce-records'>
                        {additionalObjectsAndFieldSets.map((object) => (
                          <SalesforceRecordButton key={object.objectApiName} objectLabel={`Add ${object.objectLabel}`} objectApiName={object.objectApiName} objectOnClick={() => handleSalesforceRecordClick(object.objectApiName)} />
                        ))}
                      </div>
                    }
                    <SaveToContainer />
                  </>}
        </>}
      </div>
    </div>
  );
};

export default Calendar;
